import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Avatar,
	Chip,
	TextField,
	Box,
	Typography,
	CircularProgress,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	IconButton,
	Tooltip,
	InputAdornment,
} from "@mui/material";
import {
	Search as SearchIcon,
	AddCircleOutline as AddCircleOutlineIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { searchFreelancerProfilesAsync } from "../../api/freelancer-profiles-api";
import {
	useAssignClientTalentMatching,
	useAdminClientTalentMatchingsForClient,
} from "../../hooks/useAnonymizedTalents";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { stringAvatar } from "../../utils/avatarUtils";

interface AssignTalentDialogProps {
	clientUserId: number;
	open: boolean;
	onClose: () => void;
}

/**
 * Search-and-pick dialog to manually assign a talent to a client's matched batch.
 * Reuses the freelancer-profile search engine (any profile, no eligibility
 * filter — it is a manual override) and posts the assignment per row.
 */
export const AssignTalentDialog: React.FC<AssignTalentDialogProps> = ({
	clientUserId,
	open,
	onClose,
}) => {
	const { t, i18n } = useTranslation();
	const { showSuccess } = useSnackbarContext();
	const assignMutation = useAssignClientTalentMatching();
	// Talents already in the client's current batch — hidden from the picker so
	// the CSM doesn't re-add (and get a misleading success on) an existing match.
	const { data: matchingRows } = useAdminClientTalentMatchingsForClient(
		clientUserId,
		open
	);
	const currentProfileIds = new Set(
		matchingRows?.[0]?.matchings.map((m) => m.freelancerProfileId) ?? []
	);

	const [nameInput, setNameInput] = useState("");
	const [debouncedName, setDebouncedName] = useState("");
	// Profiles already added during this dialog session — hidden from the results
	// so the CSM can't re-click an already-assigned talent.
	const [addedIds, setAddedIds] = useState<Set<number>>(new Set());

	// Debounce the search input so we don't fire a request per keystroke.
	useEffect(() => {
		const id = setTimeout(() => setDebouncedName(nameInput.trim()), 400);
		return () => clearTimeout(id);
	}, [nameInput]);

	// Reset state when the dialog is closed.
	useEffect(() => {
		if (!open) {
			setNameInput("");
			setDebouncedName("");
			setAddedIds(new Set());
		}
	}, [open]);

	const canSearch = debouncedName.length >= 2;

	const { data, isLoading, isError } = useQuery({
		queryKey: ["assign-talent-search", debouncedName, i18n.language],
		queryFn: () =>
			searchFreelancerProfilesAsync({
				name: debouncedName,
				languageCode: i18n.language,
				page: 1,
				pageSize: 10,
			}),
		enabled: open && canSearch,
		staleTime: 30 * 1000,
	});

	const results = data?.items ?? [];
	const visibleResults = results.filter(
		(p) => !addedIds.has(p.id) && !currentProfileIds.has(p.id)
	);

	const handleAssign = (freelancerProfileId: number, label: string) => {
		assignMutation.mutate(
			{ clientUserId, freelancerProfileId },
			{
				onSuccess: () => {
					setAddedIds((prev) => new Set(prev).add(freelancerProfileId));
					showSuccess(
						t("anonymizedTalent.adminAuditAssignSuccess", { name: label })
					);
				},
			}
		);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{t("anonymizedTalent.adminAuditAssignTitle")}</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					fullWidth
					size="small"
					placeholder={t("anonymizedTalent.adminAuditAssignSearchPlaceholder")}
					value={nameInput}
					onChange={(e) => setNameInput(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
					}}
					sx={{ mb: 2, mt: 1 }}
				/>

				{!canSearch && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ py: 2, textAlign: "center" }}
					>
						{t("anonymizedTalent.adminAuditAssignHint")}
					</Typography>
				)}

				{canSearch && isLoading && (
					<Box display="flex" justifyContent="center" sx={{ py: 3 }}>
						<CircularProgress size={24} />
					</Box>
				)}

				{canSearch && !isLoading && (isError || visibleResults.length === 0) && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ py: 2, textAlign: "center" }}
					>
						{t("anonymizedTalent.adminAuditAssignNoResults")}
					</Typography>
				)}

				{visibleResults.length > 0 && (
					<List dense disablePadding>
						{visibleResults.map((p) => {
							const name =
								`${p.freelancer?.user?.firstName ?? ""} ${
									p.freelancer?.user?.lastName ?? ""
								}`.trim() || "—";
							const photoUrl = p.freelancer?.picture?.urlPath
								? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${p.freelancer.picture.urlPath}`
								: undefined;
							const expertise = p.expertise?.label
								? t(`Expertises.${p.expertise.label}`, p.expertise.label)
								: "";
							const secondary = [expertise, p.title]
								.filter(Boolean)
								.join(" · ");
							const isAccepted =
								p.onboarding?.currentStatus ===
								FreelancerOnboardingStatus.Accepted;
							return (
								<ListItem
									key={p.id}
									divider
									secondaryAction={
										<Tooltip
											title={t("anonymizedTalent.adminAuditAssignAdd")}
										>
											<IconButton
												edge="end"
												color="secondary"
												disabled={assignMutation.isPending}
												onClick={() => handleAssign(p.id, name)}
											>
												<AddCircleOutlineIcon />
											</IconButton>
										</Tooltip>
									}
								>
									<ListItemAvatar>
										<Avatar
											src={photoUrl}
											{...stringAvatar(
												p.freelancer?.user?.firstName,
												p.freelancer?.user?.lastName,
												40
											)}
										/>
									</ListItemAvatar>
									<ListItemText
										disableTypography
										primary={
											<Box
												display="flex"
												alignItems="center"
												gap={0.75}
												flexWrap="wrap"
											>
												<Typography variant="body2" fontWeight={500}>
													{name}
												</Typography>
												{isAccepted && (
													<Chip
														size="small"
														variant="outlined"
														color="success"
														label={t(
															"anonymizedTalent.adminAuditAssignAccepted"
														)}
													/>
												)}
												{p.isControlled && (
													<Chip
														size="small"
														variant="outlined"
														color="primary"
														label={t(
															"anonymizedTalent.adminAuditAssignControlled"
														)}
													/>
												)}
											</Box>
										}
										secondary={
											secondary ? (
												<Typography
													variant="caption"
													color="text.secondary"
												>
													{secondary}
												</Typography>
											) : undefined
										}
									/>
								</ListItem>
							);
						})}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t("common.close")}</Button>
			</DialogActions>
		</Dialog>
	);
};
